import React from 'react';
import {
  AlertTriangle,
  Trash2,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  entityName?: string;
  entityDetails?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
  entityName,
  entityDetails,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: Trash2,
          iconColor: 'text-red-500',
          iconBg: 'bg-red-50',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconColor: 'text-amber-500',
          iconBg: 'bg-amber-50',
          confirmButton: 'bg-amber-600 hover:bg-amber-700 text-white',
        };
      case 'info':
      default:
        return {
          icon: AlertTriangle,
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-50',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white',
        };
    }
  };

  const variantStyles = getVariantStyles();
  const Icon = variantStyles.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-syne font-medium text-vitl-navy text-center">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center text-center space-y-4 py-4">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full ${variantStyles.iconBg} flex items-center justify-center`}>
            <Icon className={`w-8 h-8 ${variantStyles.iconColor}`} />
          </div>

          {/* Entity Information */}
          {entityName && (
            <div className="w-full p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-center">
                <h3 className="font-syne font-medium text-vitl-navy text-lg mb-1">
                  {entityName}
                </h3>
                {entityDetails && (
                  <p className="text-gray-600 font-lato text-sm">
                    {entityDetails}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Message */}
          <div className="space-y-2">
            <p className="text-vitl-dark font-lato text-sm leading-relaxed">
              {message}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="font-lato font-medium border-gray-300 text-vitl-navy hover:bg-gray-50"
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className={`font-lato font-medium ${variantStyles.confirmButton} ${loading ? 'opacity-75' : ''}`}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Processing...</span>
              </div>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationModal;